// src/context/RegionContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { getRegion, setRegion, clearRegion } from '../services/api';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const RegionContext = createContext();

export const useRegion = () => {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error('useRegion must be used within a RegionProvider');
  }
  return context;
};

export const RegionProvider = ({ children }) => {
  const [selectedRegion, setSelectedRegion] = useState(getRegion());
  const [allowedRegions, setAllowedRegions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const canAccessRegion = (regionId) => {
    return allowedRegions.includes(regionId);
  };

  const loadUserRegionAccess = async (userId) => {
    try {
      const userDoc = await getDoc(doc(getFirestore(), 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userAllowedRegions = userData.allowedRegions || [userData.defaultRegion || 'vquest'];
        setAllowedRegions(userAllowedRegions);
        
        // If current region is not allowed, switch to default
        if (!userAllowedRegions.includes(selectedRegion)) {
          const defaultRegion = userData.defaultRegion || userAllowedRegions[0];
          updateRegion(defaultRegion);
        }
      }
    } catch (error) {
      console.error('❌ Error loading user region access:', error);
    }
  };

  // Update region and localStorage
   const updateRegion = (regionId) => {
    if (allowedRegions.length === 0 || canAccessRegion(regionId)) {
      setSelectedRegion(regionId);
      setRegion(regionId);
      console.log("🌏 Region updated to:", regionId);
    } else {
      console.error("❌ Access denied to region:", regionId);
    }
  };

  // Clear region
  const resetRegion = () => {
    setSelectedRegion(null);
    clearRegion();
  };

  // Initialize region on mount
  useEffect(() => {
    const storedRegion = getRegion();
    if (storedRegion) {
      setSelectedRegion(storedRegion);
    }
  }, []);

  const value = {
    selectedRegion,
    allowedRegions,
    updateRegion,
    resetRegion,
    canAccessRegion,
    loadUserRegionAccess,
    isLoading,
    setIsLoading
  };

  // Add this to your RegionContext.js useEffect

useEffect(() => {
  const loadUserRegionAccessOnLogin = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (user) {
      await loadUserRegionAccess(user.uid);
    }
  };

  // Listen for auth state changes
  const auth = getAuth();
  const unsubscribe = onAuthStateChanged(auth, (user) => {
    if (user) {
      loadUserRegionAccessOnLogin();
    }
  });

  return () => unsubscribe();
}, []);


  return (
    <RegionContext.Provider value={value}>
      {children}
    </RegionContext.Provider>
  );
};
